import { TextPairing } from '@components/TextPairing';
import { Box } from '@components/Box';
import { styled } from '@styledComponents';
import { themeColor, themeMediaQuery, themeSpace } from '@utils/styledTheme.util';
import Carousel from 'nuka-carousel';
import { PlaceholderCloudImage } from '@components/CloudImage';
import { Flex } from '@components/Flex';
import { Text } from '@components/typography';

// This controls height of CustomizeTheme section. Needs a unit, can't be a Percentage here.
export const ContentWrapper = styled(Box)`
  position: relative;
  height: 100vh;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  max-height: 600vh;
  background: ${themeColor('mono7')};

  ${themeMediaQuery('below_sm')} {
    flex-direction: column;
    height: 100%;
    background: ${themeColor('humana1Light')};
  }

  &.pinned {
    position: fixed;
  }
`;

export const HeadingWrpper = styled(Box)<{ isScrolling?: boolean }>`
  width: 100%;
  top: ${themeSpace(11)};
  opacity: ${props => (props.isScrolling ? 0 : 1)};
  transition: opacity 1s;
  position: absolute;
  ${themeMediaQuery('below_sm')} {
    padding-top: ${themeSpace(11)};
    position: initial;
  }
`;

export const HeadingContainer = styled(Box)`
  width: 50%;
  margin-left: auto;
  margin-right: auto;
  ${themeMediaQuery('below_sm')} {
    width: 100%;
  }
  ${themeMediaQuery('sm')} {
    width: 70%;
  }
  ${themeMediaQuery('md')} {
    width: 60%;
  }
  ${themeMediaQuery('xl')} {
    width: 50%;
  }
`;

export const TitleText = styled(Text)`
  display: block;
  width: 287px;
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

export const SubTitleText = styled(Text)`
  display: block;
  width: 409px;
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

export const TitleTextMobile = styled(Text)`
  display: none;
  ${themeMediaQuery('below_sm')} {
    width: 169px;
    display: block;
    margin-bottom: ${themeSpace(5)};
  }
`;

export const SubTitleTextMobile = styled(Text)`
  display: none;
  ${themeMediaQuery('below_sm')} {
    width: 236px;
    display: block;
  }
`;

// new highlights box!
export const HighlightsContainer = styled(Box)`
  position: relative;
  width: 310px;
  margin-left: ${themeSpace(12)};
  margin-right: auto;
  height: 100%;

  ${themeMediaQuery('below_sm')} {
    width: 100%;
    height: 400px;
    margin: 0px;
    margin-top: ${themeSpace(6)};
    overflow: hidden;
  }
  ${themeMediaQuery('sm')} {
    margin-left: ${themeSpace(2)};
  }
  ${themeMediaQuery('md')} {
    margin-left: ${themeSpace(10)};
  }
  ${themeMediaQuery('lg')} {
    margin-left: ${themeSpace(12)};
  }
`;

export const CarouselWrapper = styled(Box)<{ activeIndex: number }>`
  ${themeMediaQuery('below_sm')} {
    position: absolute;
    display: flex;
    flex-direction: row;
    left: ${props => -80 * (props.activeIndex || 0) + 10}vw;
    transition: all 0.3s ease;
  }
`;

export const CarouselHorizontalWrapper = styled(Carousel)`
  ${themeMediaQuery('below_sm')} {
    position: absolute;
    display: flex;
    flex-direction: row;
    left: 10vw;
    transition: all 0.3s ease;
  }
`;

export const HighlightsBlock = styled(TextPairing).attrs({ className: 'customize-theme-highlights-block' })`
  position: absolute;
  top: 100%;
  margin-left: ${themeSpace(6)};

  ${themeMediaQuery('sm')} {
    max-width: 310px;
    width: 310px;
    margin-left: 0px;
  }

  ${themeMediaQuery('below_sm')} {
    position: relative;
    top: 0%;
    max-width: 80vw;
    width: 80vw;
    margin: 0px;
    padding-left: ${themeSpace(5)};
    padding-right: ${themeSpace(5)};
  }
`;

export const HandCraftedDesignsBlock = styled(HighlightsBlock)`
  top: 50% !important;

  ${themeMediaQuery('below_sm')} {
    top: 0% !important;
    .text-pairing-title {
      width: 179px;
    }
    .text-pairing-body {
      width: 256px;
    }
  }
`;

export const StyledDotIndicator = styled.div`
  margin-left: 10vw;
  padding-left: ${themeSpace(5)};
  position: absolute;
  bottom: ${themeSpace(12)};
  display: flex;
  z-index: 1;
  ul {
    display: flex;
    div + div {
      margin-left: 16px;
    }
  }
`;

export const StyledCarouselDot = styled.div<{ isActive?: boolean }>`
  cursor: pointer;
  height: 12px;
  width: 12px;
  background: ${props => (props.isActive ? themeColor('linkPress') : 'transparent')};
  border: 2px solid transparent;
  border-color: ${props => (props.isActive ? 'transparent' : themeColor('linkPress'))};
  border-radius: 50%;
`;

export const MobileHighlightSectionWrapper = styled(Box)`
  display: none;
  ${themeMediaQuery('below_sm')} {
    display: unset;
  }
`;

export const DesktopHighlightSectionWrapper = styled(Box)`
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

export const DesignPlaceholderCloudImage = styled(PlaceholderCloudImage)`
  width: 90%;
  height: 100%;
  ${themeMediaQuery('below_sm')} {
    width: 100%;
    height: 100%;
    min-height: 223px;
    margin-left: -20px;
  }
`;

export const DesignImageBox = styled(Box).attrs({ className: 'customized-theme-image-block' })`
  display: none;
  opacity: 0;
  &.design-image-visible {
    display: block;
    animation: fadeinout 2s linear 200ms forwards;
    opacity: 0.2;
    @keyframes fadeinout {
      20% {
        opacity: 0.4;
      }
      40% {
        opacity: 0.6;
      }
      80% {
        opacity: 0.8;
      }
      100% {
        opacity: 1;
      }
    }
  }
`;

export const DesignImageBoxFirstItem = styled(DesignImageBox)`
  display: block;
  opacity: 1;
  &.design-image-hide {
    display: none;
    opacity: 0;
  }
`;

export const DesignImagesWrapper = styled(Flex)`
  width: 60%;
  height: 100%;
  overflow: hidden;
  flex-direction: column;
  justify-content: center;
  ${themeMediaQuery('below_sm')} {
    width: 95%;
  }
`;
